import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { wz } from '../components/wizard-tokens';
import { MiSpinner, WizCard, WizChip, WizHeading, WizLink } from '../components/wizard-ui';
import { FlowRow } from '../campaign-wizard.types';
import FlowPlanList from './messages/flow-plan-list';
import FlowTestCard from './messages/flow-test-card';
import { planViewState, withWaitMinutes } from './messages/flow-view.logic';
import { useFlowTest } from './messages/use-flow-test';
import WaitTimeDialog from './messages/wait-time-dialog';
import type { WizardStepProps } from './step.types';

const ACCOUNT_KEYS = {
  recruiter: 'wizApprAccountRecruiter',
  sales_navigator: 'wizApprAccountSalesNav',
  normal: 'wizApprAccountNormal',
} as const;

/** Schritt 6 „Nachrichten“: Regeln, Anzahl der Chat-Nachrichten, Ablauf und „Flow testen“. */
const StepMessages: React.FC<WizardStepProps> = ({ wizard }) => {
  const { t } = useTranslation();
  const { state, patch, flowPlan, flowPlanLoading, flowPlanFailed, account } = wizard;
  // Fehlertext erst nach einem echten Fehler; bis der Plan da ist, steht „wird berechnet“.
  const planPending = planViewState({ loading: flowPlanLoading, hasPlan: flowPlan !== null, failed: flowPlanFailed }) === 'loading' || flowPlanLoading;
  const test = useFlowTest(wizard);
  const [editRow, setEditRow] = useState<FlowRow | null>(null);

  const summary =
    flowPlan === null
      ? ''
      : `${t('wizMsgSummary', { count: flowPlan.msgCount })}${flowPlan.inmails > 0 ? t('wizMsgSummaryInmails', { count: flowPlan.inmails }) : ''} · ${t(ACCOUNT_KEYS[account?.productType ?? 'normal'])}`;

  return (
    <>
      <WizCard gap={20}>
        <WizHeading title={t('wizMsgTitle')} subtitle={t('wizMsgSubtitle')} />
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
          {(flowPlan?.rules ?? []).map(rule => (
            <WizChip key={rule} variant="grey">
              {rule}
            </WizChip>
          ))}
          <span style={{ marginLeft: 4, fontSize: 13 }}>
            <WizLink onClick={() => wizard.goTo(5)}>{t('wizMsgChangeApproach')}</WizLink>
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 16,
            background: wz.page,
            borderRadius: 10,
            padding: '12px 16px',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap' }}>
            {t('wizMsgChatCount')}
          </div>
          <input
            type="range"
            min={1}
            max={6}
            step={1}
            value={state.chatCount}
            onChange={e => patch({ chatCount: Number(e.target.value) as 1 | 2 | 3 | 4 | 5 | 6 })}
            aria-label={t('wizMsgChatCountAria', { count: state.chatCount })}
            style={{ flex: 1, accentColor: wz.primary, cursor: 'pointer' }}
          />
          <div
            style={{ fontSize: 15, fontWeight: 700, minWidth: 16, textAlign: 'center' }}
            data-testid="wiz-chat-count"
          >
            {state.chatCount}
          </div>
          <div style={{ fontSize: 13, color: wz.muted, whiteSpace: 'nowrap' }}>
            {t('wizMsgChatCountHint')}
          </div>
        </div>
        <div
          style={{ fontSize: 13, color: wz.muted, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {summary}
          {planPending ? <MiSpinner size={14} /> : null}
        </div>
        {flowPlan ? (
          <FlowPlanList rows={flowPlan.rows} onEditWait={setEditRow} />
        ) : (
          <div style={{ fontSize: 14, color: wz.muted }}>
            {planPending ? t('wizMsgPlanLoading') : t('wizMsgPlanError')}
          </div>
        )}
      </WizCard>
      <FlowTestCard test={test} />
      <WaitTimeDialog
        row={editRow}
        onClose={() => setEditRow(null)}
        onSave={(waitKey, minutes, defaultMinutes) => {
          patch({ waitMinutes: withWaitMinutes(state.waitMinutes, waitKey, minutes, defaultMinutes) });
          setEditRow(null);
        }}
      />
    </>
  );
};

export default StepMessages;
